/**
 * Chart visit-note LIST, read from Elation's own client-side model.
 *
 * Why this exists at all: the shipped list came from a DOM scrape of the chart
 * feed (`#chart-feed-list li.el8FeedItemVisitNote`, see the Elation adapter).
 * Live verification against a sandbox chart showed two hard limits of that
 * source, neither of which a better selector can fix:
 *
 *  1. The feed contains ONLY SIGNED notes. On a chart with 4 visit notes (1
 *     signed, 3 unsigned) the feed rendered exactly 1 matching `li`. An unsigned
 *     draft never gets the `el8FeedItemVisitNote` class, so it can only appear
 *     in the panel once the clinician OPENS it (via the open-note path) — the
 *     "my draft isn't listed" bug.
 *  2. The feed renders ASYNCHRONOUSLY. A scrape that beats it returns an empty
 *     list, which is why the tab could sit empty after a page refresh.
 *
 * `window.el8.ptdata.visitNotes` is the collection the chart itself renders
 * from: it is populated with the chart (no feed race) and holds signed AND
 * unsigned notes with their status flags. Verified live 2026-08-05 — the
 * collection exposes `all()/sorted()/getSigned()/getUnsigned()/getById()` and
 * each note exposes `getId()/getDocumentDate()/getDisplayDate()/
 * getDescription()/getNoteSummary()/getHeaderSummary()/getPhysicianFullName()/
 * getSignDate()/getSignUserName()/getTemplate()/getTemplateClass()/
 * getNoteTypeId()/isSignedOff()/isSaved()/isPreview()/isOnHold()/isDeleted()`.
 *
 * `window.el8` exists ONLY in the page MAIN world, so the read below is
 * injected there with `chrome.scripting.executeScript({ world: "MAIN" })` —
 * the same mechanism `stageInPage` / `readNoteSectionsInPage` already use. The
 * DOM scrape is KEPT as the fallback: when el8/ptdata is unavailable (a chart
 * layout we haven't seen, a blocked injection, a future Elation refactor) the
 * panel falls back to exactly the list it shows today, so this can only add.
 *
 * The el8 model gives us the note's IDENTITY and STATUS, not its body text —
 * bodies still come from the feed scrape, merged in by `buildChartNotes`.
 */
import type { ElationNote } from "../../src/bridge/types";

/**
 * One visit note as read from `el8.ptdata.visitNotes`, flattened to plain
 * serializable values. This crosses the `executeScript` boundary, so every
 * field is a primitive and every reader treats it as UNTRUSTED.
 */
export interface ChartNoteRead {
  /** `getId()` — Elation's numeric note id, as a string. */
  id: string;
  /** `getDocumentDate()` — the note's document/visit date. */
  documentDate: string;
  /** `getDisplayDate()` — the date Elation itself renders for this note. */
  displayDate: string;
  /** `getDescription()` — the note's description / chief complaint line. */
  description: string;
  /** `getNoteSummary()` — Elation's one-line summary of the note. */
  noteSummary: string;
  /** `getHeaderSummary()` — the summary Elation renders in the note header. */
  headerSummary: string;
  /** `getPhysicianFullName()` — the note's physician. */
  physician: string;
  /** `getSignDate()` — empty for unsigned notes. */
  signDate: string;
  /** `getSignUserName()` — who signed it; empty for unsigned notes. */
  signUserName: string;
  /** `getTemplate()` — the note's template ("Simple Note", "SOAP Note", …). */
  templateName: string;
  /** `getTemplateClass()` — the template's class, a coarser type signal. */
  templateClass: string;
  /** `isSignedOff()` */
  signed: boolean;
  /** `isSaved()` — the note has been persisted (a real draft, not a stub). */
  saved: boolean;
  /** `isPreview()` — a preview/scratch note, never a chart document. */
  preview: boolean;
  /** `isOnHold()` — parked by the clinician, still unsigned. */
  onHold: boolean;
  /** `isDeleted()` */
  deleted: boolean;
  /**
   * The note's BODY, read from its own model rather than the chart feed.
   *
   * `getBulletSets()` returns a per-category collection and each one answers
   * `asSingleField()` with that category's text — live-verified 2026-08-06 across
   * five notes on one chart. This is the only body source that works for an
   * UNSIGNED draft: the feed renders signed notes only, so a clinician's own
   * in-progress note could never show content.
   *
   * Absent (not empty) when the model had no bullet sets at all, which is how
   * `buildChartNotes` knows to fall back to the scraped body instead.
   */
  body?: ChartNoteBody;
}

/**
 * Elation bullet categories, flattened. Named for the categories themselves
 * rather than our section vocabulary, because the mapping to a rendered note is
 * template-dependent and belongs at the point of use.
 *
 * `narrative` matters more than it looks: on a `oneCol.oneField` template — the
 * commonest live — the ENTIRE note is one Narrative field, and every other
 * category is legitimately empty.
 */
export interface ChartNoteBody {
  narrative: string;
  hpi: string;
  problem: string;
  objective: string;
  assessment: string;
  assessplan: string;
  plan: string;
  ros: string;
  pe: string;
  followup: string;
  reason: string;
  instr: string;
  /**
   * Text from bullet categories with no canonical field here, each labelled
   * with its own category name.
   *
   * Bullet categories are template-dependent (they are the note's section keys,
   * capitalized) and a two-column layout adds COLUMN-scoped ones such as
   * `ElationNoteColOne`. Rather than drop prose that does not match a name we
   * happen to know, keep it — labelled, so it is never shown under a heading it
   * did not come from — and surface it only when nothing else produced a body.
   */
  extra?: string;
}

/** What the MAIN-world read returns. */
export interface ChartNotesReadResult {
  /**
   * True only when `el8.ptdata.visitNotes` was reachable AND enumerable. False
   * means "no model read happened" — callers MUST fall back to the DOM scrape
   * rather than treat an empty `notes` as "this chart has no notes".
   */
  available: boolean;
  notes: ChartNoteRead[];
  /**
   * The currently-open note's id, when a note is open. Used to drop that note
   * from the listed set: the panel renders the open note separately (with its
   * live body), so listing it again would duplicate the row.
   */
  openNoteId?: string;
  /**
   * WHY the read produced nothing, when it produced nothing. Reported for the
   * same reason `readNoteSectionsInPage` reports it: the two situations are not
   * the same fact about the chart.
   *
   *  - `"patient-mismatch"` — the page names a patient and it is NOT the one the
   *    caller asked about. This read is looking at the wrong chart.
   *  - `"unreadable"` — no model, no enumerable collection, or the page names no
   *    patient at all (Elation mid-navigation, `location.pathname` between
   *    charts). Identity is UNKNOWN, not wrong.
   *
   * Absent when `available` is true. Nothing branches on the distinction today;
   * it exists so the reason is not thrown away at the point it is known, and so
   * a future caller does not have to re-derive it from a bare `false`.
   */
  failure?: "patient-mismatch" | "unreadable";
}

/**
 * Whether the chart's note list is a FACT about the chart, or merely what we
 * managed to read.
 *
 * This is the distinction the Notes tab was missing. `buildChartNotes` returns
 * `[]` for both "this chart has no notes" and "neither source could tell us",
 * and the panel — having only the array — rendered the first sentence for both:
 * "No visit notes in this chart yet." On an EHR patient switch that is a false
 * statement about a chart with a decade of notes in it, made in the one window
 * where BOTH sources are legitimately empty (the model read is unavailable while
 * Elation navigates, and the chart feed renders asynchronously — the two limits
 * documented at the top of this file).
 *
 * The rule, and why each half of it:
 *
 *  - `"loaded"` — the MODEL read answered (`available`). It is the only source
 *    with patient attribution (the in-page guard in `readChartNotesInPage`
 *    refused any chart that is not the expected patient's), and it is populated
 *    with the chart, so once it answers, `[]` means an empty chart.
 *  - `"unverified"` — the model read did NOT answer, but the DOM feed produced at
 *    least one note. A note in the feed proves notes exist (an async feed cannot
 *    invent rows), so the list is real — but the feed scrape has NO patient
 *    attribution of any kind, and in an in-tab switch window those rows can still
 *    be the previous patient's. `"loaded"` used to vouch for this path too, which
 *    labelled the one unattributed list in the system a FACT about the chart.
 *    Consumers may render the rows; nothing may treat them as verified.
 *  - `"unread"` — neither source produced anything. An empty feed proves nothing
 *    at all, in either direction.
 *  - a read refused for `"patient-mismatch"` is `"unread"` no matter what the
 *    feed holds: the page positively identified itself as a DIFFERENT patient's
 *    chart, so the same page's feed rows are wrongly attributed for certain.
 *    `buildChartNotes` drops them for the same reason.
 *
 * Anything not `"loaded"` the panel must never render as "no notes". Erring this
 * way is deliberate: a loading line on a chart that really has no notes is
 * corrected by the next poll a second later, while "this patient has no visit
 * notes" is a clinical claim we cannot support.
 */
export type ChartNotesListStatus = "loaded" | "unverified" | "unread";

export function chartNotesListStatus(
  read: ChartNotesReadResult | null | undefined,
  scraped: ElationNote[] | null | undefined,
): ChartNotesListStatus {
  // The page said it is ANOTHER patient's chart. Its feed rows are that other
  // patient's too — fail closed, exactly like the blank-expected-id guard.
  if (read?.failure === "patient-mismatch") return "unread";
  if (read?.available === true && Array.isArray(read.notes)) return "loaded";
  return Array.isArray(scraped) && scraped.length > 0 ? "unverified" : "unread";
}

/** Per-note status surfaced to the UI so it can badge a draft as a draft. */
export type ChartNoteStatus = "signed" | "draft" | "on-hold";

/**
 * WHICH notes the Notes tab lists — the single policy switch.
 *
 * Default policy: every SIGNED note, plus the unsigned notes that are real
 * chart work — SAVED drafts and ON-HOLD notes. Excluded:
 *  - `isDeleted()` — not in the chart at all.
 *  - `isPreview()` — Elation's scratch/preview instances. These are not
 *    documents the clinician has committed to; listing them would put rows in
 *    the panel that never appear in Elation's own chart view.
 *
 * Deliberately one named predicate over the flat `ChartNoteRead` record rather
 * than a filter spread through the read: changing the policy (e.g. "list
 * previews too", "hide on-hold") is a one-line edit here plus a test, and the
 * MAIN-world read stays a dumb, faithful dump of the model.
 */
export function shouldListChartNote(note: ChartNoteRead): boolean {
  if (note.deleted) return false;
  if (note.preview) return false;
  if (note.signed) return true;
  return note.saved || note.onHold;
}

/** Signed wins over on-hold: a signed note is done, whatever it was before. */
export function chartNoteStatus(note: ChartNoteRead): ChartNoteStatus {
  if (note.signed) return "signed";
  if (note.onHold) return "on-hold";
  return "draft";
}

/**
 * Injected into the Elation tab's MAIN world via
 * `chrome.scripting.executeScript({ world: "MAIN", func: readChartNotesInPage })`.
 *
 * MUST be fully self-contained — executeScript serializes the function, so it
 * cannot reference ANYTHING outside its own body (no imports, no module-scope
 * consts, no calls to other functions in this module, not even the types above
 * at runtime). Every helper is inlined for that reason.
 *
 * Fails soft to `{ available: false, notes: [] }` on any missing member or
 * throw, which is the signal for the caller to keep the DOM-scraped list.
 *
 * `expectedPatientId` is the chart the CALLER believes it is reading, taken from
 * the tab URL. It is the same guard `readNoteSectionsInPage` and `stageInPage`
 * already apply, added here for the same reason and with the same wording, and it
 * is why this read is no longer the only MAIN-world read on this page with no
 * patient attribution at all: `getActiveEhrTab` prefers the FOREGROUND EHR tab,
 * so with patient A in one tab and B in another this read can land in B and its
 * notes would be listed under A's chart. The in-tab switch is the same hazard on
 * one tab: the patient poll is a separate, up-to-10s channel, so for that window
 * the caller still thinks it is looking at A.
 *
 * BLANK OR OMITTED `expectedPatientId` DROPS EVERYTHING. That is the safe
 * direction, and it is the direction the imaging read already takes
 * (`elationReportsRead.ts`: "An empty or missing `expectedPatientId` drops
 * EVERYTHING. That is the safe direction.").
 *
 * An earlier version of this comment cited that read as precedent for the
 * OPPOSITE behaviour — disabling the guard on a blank id, which is what
 * `stageInPage` does — and the code matched the comment rather than the
 * precedent. The consequence: on any Elation view where the tab URL has no
 * `/patient/<id>` to parse (a dashboard, an appointment view) while the SPA still
 * holds the previously-opened chart in `el8.ptdata.visitNotes`, the guard was
 * skipped and the PREVIOUS patient's notes were returned labelled "loaded" — i.e.
 * attribution fell back to whatever the DOM scrape claimed, which is the exact
 * hazard this guard was added to close.
 *
 * Nobody produced a real URL that triggers it, so treat the exploit as unproven
 * and the direction as simply wrong: a read that cannot say whose chart it read
 * must not hand its rows to a caller that will label them. Refusing costs a
 * fallback to the DOM feed; failing open costs one patient's notes under another
 * patient's name.
 *
 * A non-Elation adapter never reaches this function — the read is Elation-only —
 * so nothing legitimate depends on the permissive branch.
 */
export function readChartNotesInPage(
  expectedPatientId?: string,
): ChartNotesReadResult {
  try {
    // ── Patient-match guard ───────────────────────────────────────────
    // Only the page knows which chart it actually is. Refuse rather than return
    // notes the caller will attribute to the wrong patient — and report WHICH of
    // the two refusals this is (see `ChartNotesReadResult.failure`).
    const livePatientId = location.pathname.match(/\/patient\/(\d+)/)?.[1];
    // FAIL CLOSED IN BOTH DIRECTIONS: a mismatch, and a caller that could not say
    // which chart it expected. See the doc comment above for why the second half
    // is not optional.
    if (!expectedPatientId || livePatientId !== expectedPatientId) {
      return {
        available: false,
        notes: [],
        failure:
          livePatientId && expectedPatientId ? "patient-mismatch" : "unreadable",
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const el8: any = (window as any).el8;
    const collection = el8 && el8.ptdata && el8.ptdata.visitNotes;
    if (!collection) return { available: false, notes: [], failure: "unreadable" };

    // `sorted()` is Elation's own ordering; `all()` is the raw set. Either is
    // fine — the caller re-sorts by date — so take whichever exists.
    let list: unknown = null;
    try {
      if (typeof collection.sorted === "function") list = collection.sorted();
      else if (typeof collection.all === "function") list = collection.all();
    } catch {
      list = null;
    }
    if (!Array.isArray(list)) return { available: false, notes: [], failure: "unreadable" };

    // Call a zero-arg getter and coerce to a trimmed string. Elation's getters
    // are not uniformly typed (`getTemplate()` can return an object), so unwrap
    // the common name-ish shapes rather than stringifying "[object Object]".
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const str = (obj: any, method: string): string => {
      try {
        if (!obj || typeof obj[method] !== "function") return "";
        const v = obj[method]();
        if (v == null) return "";
        if (typeof v === "object") {
          const named = v.displayName || v.name || v.title || v.label;
          return typeof named === "string" ? named.trim() : "";
        }
        return String(v).trim();
      } catch {
        return "";
      }
    };

    // Call a zero-arg predicate. An absent/throwing predicate is `false`, which
    // is the conservative answer for every flag here: unknown-`isDeleted` must
    // not hide a real note, and unknown-`isSigned` must not claim a draft is
    // signed. `shouldListChartNote` then keeps such a note only if it is saved.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bool = (obj: any, method: string): boolean => {
      try {
        return !!(obj && typeof obj[method] === "function" && obj[method]());
      } catch {
        return false;
      }
    };

    // Elation's date getters return a DATE-LIKE OBJECT, not a string —
    // live-verified 2026-08-06: `getDisplayDate()` gave
    // `{_useCache,_tzInfo,_day,year,month,date,hours,minutes}`. `str()` unwraps
    // objects by looking for displayName/name/title/label, finds none of them,
    // and returns "" — so EVERY note came back with no date. That silently
    // emptied the whole list: `findScrapedBody` bails on a missing date, so
    // every note fell through to EMPTY_BODY and the panel rendered headers with
    // no content.
    //
    // `getNoteSummary()` is the reliable source and needs no assumptions: it is
    // "08/05/2026 visit note." Parsed rather than read off the object because
    // the object's `month` may or may not be 0-based, and that is exactly the
    // kind of guess that produced this bug.
    // Layered, not replaced: a getter that DOES hand back a usable string still
    // wins, and the summary parse is the fallback for the object case. Reading
    // the summary unconditionally would discard a real date on any Elation build
    // whose getters return strings.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dateStr = (obj: any): string => {
      const direct = str(obj, "getDisplayDate") || str(obj, "getDocumentDate");
      if (direct) return direct;
      const m = str(obj, "getNoteSummary").match(/\b(\d{1,2}\/\d{1,2}\/\d{4})\b/);
      return m ? m[1] : "";
    };

    // The note's body, from its own model. `getBulletSets()` gives a collection
    // per Elation category and each answers `asSingleField()` with that
    // category's text. Live-verified 2026-08-06 on five notes, including three
    // UNSIGNED drafts that the feed scrape can never supply a body for.
    //
    // Returns undefined — not an object of empty strings — when there are no
    // bullet sets at all, so the caller can tell "model says this note is empty"
    // apart from "no model read happened" and fall back to the scrape.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bodyOf = (obj: any) => {
      let sets: any;
      try {
        if (!obj || typeof obj.getBulletSets !== "function") return undefined;
        sets = obj.getBulletSets();
      } catch {
        return undefined;
      }
      if (!sets || typeof sets !== "object") return undefined;

      // One bullet set's text. `asSingleField()` is the primary accessor and is
      // what every free-text (textarea/listeditor) category answers.
      //
      // The two fallbacks below run ONLY when it yielded nothing, so they can
      // never displace a good read. They exist because a MULTILIST category
      // holds ENTRIES rather than one field — and on `twoCol.complex2` BOTH
      // prose-bearing sections (`problem`, `assessplan`) are multilists, so a
      // single-field read is the wrong shape for the whole template. Both
      // accessors are ones this codebase already reads elsewhere
      // (`getJsonData()` in `readNoteSectionsInPage`, `getMultilistEntries()`
      // named in the `pe` findings); each is guarded, so absence costs nothing.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const textOf = (s: any): string => {
        try {
          if (s && typeof s.asSingleField === "function") {
            const v = s.asSingleField();
            if (v != null) {
              const t = String(v).trim();
              if (t) return t;
            }
          }
        } catch {
          /* fall through to the entry readers */
        }
        // STRINGS ONLY, and `.text` is the only key this codebase has evidence
        // for (`readSectionText` reads `getJsonData()` as `[{text}]`). `.value`
        // is a guess kept as a second chance, so it is narrowed to a string:
        // the natural shape for a CODED structured entry is
        // `{ text: "Hypertension", code: "I10" }`, and `String()` on that object
        // yields the literal "[object Object]" — truthy, so it survives
        // `.filter(Boolean)` and gets written into a clinical note body and into
        // the generation prompt via `priorNotes`. A shape we cannot read must
        // read as empty, never as garbage.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const join = (entries: any): string => {
          if (!Array.isArray(entries)) return "";
          return entries
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((e: any) => {
              if (typeof e === "string") return e.trim();
              if (!e || typeof e !== "object") return "";
              if (typeof e.text === "string") return e.text.trim();
              if (typeof e.value === "string") return e.value.trim();
              return "";
            })
            .filter(Boolean)
            .join("\n");
        };
        try {
          if (s && typeof s.getMultilistEntries === "function") {
            const t = join(s.getMultilistEntries());
            if (t) return t;
          }
        } catch {
          /* fall through */
        }
        try {
          if (s && typeof s.getJsonData === "function") {
            const t = join(s.getJsonData());
            if (t) return t;
          }
        } catch {
          /* nothing readable on this set */
        }
        return "";
      };

      // ENUMERATE the categories this note actually has, rather than probing a
      // fixed list of names.
      //
      // The fixed probe is why a `twoCol.complex2` note reads back with no body.
      // Bullet categories are the note's own section keys, capitalized — and
      // they are TEMPLATE-DEPENDENT (`stageInPage` builds one the same way:
      // `addBullet(capitalize(sectionKey))`). A two-column layout also carries
      // COLUMN-scoped categories — `ElationNoteColOne` was read live off a real
      // chart — which no amount of guessing at clinical names will ever match.
      // Twelve fixed lookups against a ~30-entry, template-specific map miss
      // silently and yield an all-empty body.
      //
      // On a SIGNED note that is survivable: `toListedNote` falls through to the
      // feed-scraped body. On an unsigned DRAFT it is fatal — the feed contains
      // signed notes only, so there is no fallback and the note renders
      // completely empty. That is the reported bug.
      const out = {
        narrative: "",
        hpi: "",
        problem: "",
        objective: "",
        assessment: "",
        assessplan: "",
        plan: "",
        ros: "",
        pe: "",
        followup: "",
        reason: "",
        instr: "",
        // Text from categories we do NOT have a canonical field for, each
        // labelled with its own category name so nothing is silently dropped
        // and nothing is presented under a heading it did not come from. Used
        // as an absolute last resort in `toListedNote`.
        extra: "",
      };
      // Elation's own key spellings, PLUS the long forms of the same clinical
      // sections. The aliases are not cosmetic: an unrecognized category falls
      // into `extra`, which is rendered with its raw key as a prefix — so a
      // template whose section is literally named `subjective` used to surface
      // in the panel as "Subjective: Pt reports chest pain.", putting an
      // internal category key into physician-facing text. Only unambiguous
      // spellings are aliased; a two-letter guess like `ap` is not one.
      const canonical: Record<string, keyof typeof out> = {
        narrative: "narrative",
        hpi: "hpi",
        historyofpresentillness: "hpi",
        subjective: "hpi",
        problem: "problem",
        objective: "objective",
        assessment: "assessment",
        assessplan: "assessplan",
        assessmentplan: "assessplan",
        assessmentandplan: "assessplan",
        plan: "plan",
        ros: "ros",
        reviewofsystems: "ros",
        pe: "pe",
        physicalexam: "pe",
        physicalexamination: "pe",
        followup: "followup",
        reason: "reason",
        instr: "instr",
        instructions: "instr",
      };

      let keys: string[] = [];
      try {
        keys = Object.keys(sets);
      } catch {
        keys = [];
      }
      const extras: string[] = [];
      for (const key of keys) {
        let set: any;
        try {
          set = sets[key];
        } catch {
          continue;
        }
        if (!set || typeof set !== "object") continue;
        const text = textOf(set);
        if (!text) continue;
        const norm = String(key).toLowerCase().replace(/[^a-z0-9]/g, "");
        const target = canonical[norm];
        if (target) {
          // KEEP BOTH when two categories land in the same slot. The aliases
          // above mean a template carrying an `HPI` section AND a `Subjective`
          // section maps both to `hpi`; keeping only the first DROPPED the other
          // outright, and which one survived was `Object.keys` order. That turned
          // a cosmetic defect (a raw key prefix in the panel) into content loss.
          // Identical text is not repeated — lazily-instantiated sets can appear
          // more than once under differing spellings, which is what the
          // first-wins rule was there for.
          const already = out[target];
          if (!already) out[target] = text;
          else if (!already.includes(text)) out[target] = already + "\n\n" + text;
        } else {
          extras.push(key + ": " + text);
        }
      }
      out.extra = extras.join("\n\n");
      return out;
    };

    const notes: ChartNoteRead[] = [];
    for (const raw of list) {
      const id = str(raw, "getId");
      if (!id) continue; // no identity — not something we can key or dedupe
      const parsedDate = dateStr(raw);
      notes.push({
        id,
        documentDate: parsedDate,
        displayDate: parsedDate,
        description: str(raw, "getDescription"),
        noteSummary: str(raw, "getNoteSummary"),
        headerSummary: str(raw, "getHeaderSummary"),
        physician: str(raw, "getPhysicianFullName"),
        signDate: str(raw, "getSignDate"),
        signUserName: str(raw, "getSignUserName"),
        // NOT a display name. Live-verified 2026-08-06: `getTemplate()` returns
        // Elation's LAYOUT CLASS — "oneCol.oneField", "twoCol.complex2",
        // "oneCol.simple" — and `getTemplateClass()` the coarser "oneCol". Using
        // it as the note's name put "oneCol.oneField" in the list as a heading,
        // and worse: `visitTypeWithTemplate` folds it into `visitType`, where
        // "oneCol.simple" matches /\bsimple\b/ and every note resolved to the
        // SIMPLE type. Layout-shaped values are rejected outright; the real name
        // for the OPEN note comes from `getCurrFormTemplate().displayName`.
        // Inlined mirror of LAYOUT_CLASS_PATTERN / humanTemplateName — this fn is
        // serialized by executeScript and cannot reference module scope. Kept in
        // the same literal shape so the parity test can extract it.
        templateName: (() => {
          const t = str(raw, "getTemplate");
          return /^[a-z]+[A-Za-z0-9]*(\.[A-Za-z0-9]+)+$/.test(t) ? "" : t;
        })(),
        templateClass: str(raw, "getTemplateClass"),
        signed: bool(raw, "isSignedOff"),
        saved: bool(raw, "isSaved"),
        preview: bool(raw, "isPreview"),
        onHold: bool(raw, "isOnHold"),
        deleted: bool(raw, "isDeleted"),
        body: bodyOf(raw),
      });
    }

    let openNoteId: string | undefined;
    try {
      const open =
        el8.appchart &&
        el8.appchart.mgr &&
        el8.appchart.mgr.notes &&
        typeof el8.appchart.mgr.notes.getOpenNoteId === "function"
          ? el8.appchart.mgr.notes.getOpenNoteId()
          : null;
      if (open != null && String(open).length > 0) openNoteId = String(open);
    } catch {
      /* no open note / API absent — the caller also dedupes on the DOM id */
    }

    return { available: true, notes, openNoteId };
  } catch {
    return { available: false, notes: [], failure: "unreadable" };
  }
}

// ── Pure mapping (runs in the sidepanel, fully testable) ─────────────────────

function parseDate(value: string): number | null {
  const t = value ? Date.parse(value) : NaN;
  return Number.isNaN(t) ? null : t;
}

function norm(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

/**
 * Elation's layout classes, which `getTemplate()` returns instead of a template
 * name: `oneCol.oneField`, `twoCol.complex2`, `oneCol.simple`. Dotted, no
 * whitespace, camelCase segments — nothing a practice would name a template.
 *
 * Exported for the test that pins the live-observed values, so a future template
 * whose real name happens to contain a dot cannot be silently swallowed.
 */
export const LAYOUT_CLASS_PATTERN = /^[a-z]+[A-Za-z0-9]*(\.[A-Za-z0-9]+)+$/;

/**
 * A template name fit to show a clinician, or "" when Elation gave us a layout
 * class. Returning "" rather than the raw value matters: every consumer then
 * falls back to the note's DESCRIPTION ("Office Visit Note"), which is what the
 * chart itself displays.
 */
export function humanTemplateName(value: string | undefined): string {
  const v = (value ?? "").trim();
  if (!v || LAYOUT_CLASS_PATTERN.test(v)) return "";
  return v;
}

/**
 * The el8 model knows a note's identity, date, template and status — but not
 * its BODY. Bodies only exist in the rendered chart feed, which the DOM scrape
 * already reads, so carry them across when a listed note and a scraped note are
 * plainly the same note.
 *
 * Matching is deliberately conservative: same date AND same type, or same date
 * when that date is unambiguous on both sides. A wrong match would show one
 * visit's narrative under another visit's heading, which is worse than showing
 * a header-only row.
 */
function findScrapedBody(
  note: ChartNoteRead,
  scraped: ElationNote[],
): ElationNote | null {
  const date = norm(note.displayDate) || norm(note.documentDate);
  if (!date) return null;
  const sameDate = scraped.filter((s) => norm(s.visitDate) === date);
  if (sameDate.length === 1) return sameDate[0];
  if (sameDate.length === 0) return null;
  const type = norm(note.templateName);
  const byType = type ? sameDate.filter((s) => norm(s.visitType) === type) : [];
  return byType.length === 1 ? byType[0] : null;
}

/** Whitespace-insensitive form, for comparing two renderings of one text. */
function flat(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

/**
 * One text, two renderings, compared without punting on typography.
 *
 * The feed is read with `textContent`, which DECODES entities and shows whatever
 * smart quotes / dashes Elation's own renderer emitted; the model text comes off a
 * bullet accessor and may carry the raw entity or the straight character. Comparing
 * them byte for byte made the truncation test fail on any such difference — the
 * partial value then won, publishing a clipped narrative and putting it on the
 * generation wire. Only the COMPARISON is normalized; whichever value wins is
 * returned exactly as it was read.
 *
 * Entity decoding runs first (it is what makes the two renderings comparable at
 * all) and is applied to BOTH sides, so the character mapping that follows stays
 * one-for-one and leaves line breaks where they were — `continuesMidText` still
 * needs to see them.
 */
function comparable(value: string): string {
  return value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/[‘’‚‛′]/g, "'")
    .replace(/[“”„‟″]/g, '"')
    .replace(/[‐‑‒–—―−]/g, "-")
    .replace(/[\u00a0\u2000-\u200a\u202f\u205f\u3000]/g, " ");
}

/**
 * Choose between the feed scrape and the model read for ONE prose slot.
 *
 * ── THE INVARIANT ────────────────────────────────────────────────────────────
 * **The scrape wins unless it is demonstrably the SAME text, shortened.** Never
 * "the scrape wins" and never "the longer wins" — either of those decides by
 * something other than completeness, which is how this ordering has now been
 * reversed three times.
 *
 * Stated as a property rather than a ranking, so a new template, a new bullet
 * accessor or a new feed layout cannot silently defeat it: no key name and no
 * source identity appears in the decision, only the relationship between the two
 * values. If the scrape is a proper prefix of the model text (ignoring
 * whitespace and a trailing ellipsis) the two are the same content and the model
 * has more of it, so the model wins. Otherwise they are DIFFERENT content and the
 * scrape wins — it is Elation's own rendering of what the clinician charted.
 *
 * Both directions are load-bearing and both have a reproduction behind them:
 *
 *  - scrape-more-complete: a signed `twoCol.complex2` note whose `assessplan`
 *    multilist reads "Hypertension\nType 2 diabetes" while the charted A/P prose
 *    ("HTN uncontrolled at 158/94 … start lisinopril 10mg daily") is in the feed.
 *    The list is not a shortened form of the prose, so the prose wins — the
 *    defect this file has been fixed for three times stays fixed.
 *  - model-more-complete: a feed rendering that carries less of the same text
 *    than the model does. The previous round handed the scrape UNCONDITIONAL
 *    precedence, which converted a complete model read into a partial one and
 *    made the full text unreachable. Demoting the model by identity overshot;
 *    what actually mattered was never trading a whole read for part of one.
 *
 * On reachability, since building machinery for an impossible case is its own
 * defect: a CSS-clamped or ellipsised RENDERING cannot shorten the scrape at all
 * — the adapter reads `textContent`, which ignores `line-clamp`, `overflow` and
 * `text-overflow`. What IS reachable straight from the adapter's own code is a
 * feed that renders a multi-section note as heading-LESS `.el8Preserve` blocks:
 * they are joined into `narrative` and land whole in `subjective`
 * (`adapters/elation.ts`), while the model holds the categories separately. A
 * feed value that is a shortened form of the model's is therefore not a
 * hypothetical class, and a rule that cannot be defeated costs one comparison.
 *
 * ── WHY A PREFIX IS NOT ENOUGH ON ITS OWN ────────────────────────────────────
 * A prefix relationship is evidence of TRUNCATION only when the model text
 * CONTINUES the scraped text — same sentence, same paragraph. A multilist read
 * joins its entries with "\n", so a single-diagnosis visit charted as
 * "Hypertension" is a proper prefix of the model's
 * "Hypertension\nI10\nType 2 diabetes\nE11.9" purely because the first LIST
 * ENTRY happens to equal the charted prose. Publishing the model there is the
 * original defect again — coded cruft displacing charted prose, on the field that
 * goes onto the generation wire — so the continuation must not begin at a hard
 * break (a newline or a bullet). `flat()` collapses "\n" to a space and cannot
 * see that, which is exactly how the previous round let this through: the one
 * repro it had used LONG charted prose that diverges on the first word.
 *
 * ── THE ONE EXCEPTION: THE SCRAPE SAYS SO ITSELF ─────────────────────────────
 * A trailing "…" / "..." is the feed DECLARING itself shortened, which is stronger
 * evidence than where the continuation happens to begin, so it settles the
 * question before the cut point is consulted. Without that, a rendering clipped
 * exactly at a line end kept the clipped text AND carried a literal ellipsis into
 * the field that feeds generation — the safe direction publishing the wrong value.
 * A charted prose value with no ellipsis is untouched by this, which is what keeps
 * the "Hypertension" vs coded-list repro fixed.
 */
function preferComplete(scraped: string | undefined, model: string): string {
  const s = scraped ?? "";
  if (!s) return model;
  if (!model) return s;
  // Trailing "…" / "..." is how a shortened rendering announces itself; strip it
  // before comparing, then require a PROPER prefix so identical text keeps the
  // scrape (the two sources agree and there is nothing to choose).
  const flatScrape = flat(comparable(s));
  const head = flatScrape.replace(/(?:\.{3}|…)$/, "").trim();
  const announcesTruncation = head !== flatScrape.trim();
  const whole = flat(comparable(model));
  const isPrefix =
    head.length > 0 && whole.length > head.length && whole.startsWith(head);
  if (!isPrefix) return s;
  // The scrape DECLARED itself shortened, and that is stronger evidence than
  // anything the cut point can show: a trailing ellipsis is the feed saying "there
  // is more of this text than I am showing you". Take the model without asking
  // where the continuation begins — otherwise a rendering that happens to cut at a
  // line end kept the clipped value AND put a literal "…" onto the generation
  // wire, which is the one shape where the safe direction is also the wrong one.
  if (announcesTruncation) return model;
  // The prefix holds. Now ask the question `flat()` threw away: does the model
  // CONTINUE the scraped text, or does it merely start with a structurally
  // separate item that reads the same? Anything we cannot answer keeps the
  // scrape, which is the direction every rule in this file leans.
  const rest = modelTextAfter(head, comparable(model).trim());
  if (rest === null) return s;
  return continuesMidText(rest) ? model : s;
}

/**
 * The model text that follows `head`, located in the model's UNFLATTENED form.
 *
 * Walks both strings together treating any run of whitespace in the model as the
 * single space `flat()` would have produced, so the caller can inspect what the
 * real separator at the cut point was. `null` when the prefix does not line up
 * character for character (it should not happen after the flattened prefix test,
 * but "cannot tell" must be answerable).
 */
function modelTextAfter(head: string, model: string): string | null {
  let i = 0;
  let j = 0;
  while (j < head.length) {
    if (i >= model.length) return null;
    if (/\s/.test(head[j])) {
      if (!/\s/.test(model[i])) return null;
      while (i < model.length && /\s/.test(model[i])) i++;
      j++;
      continue;
    }
    if (model[i] !== head[j]) return null;
    i++;
    j++;
  }
  return model.slice(i);
}

/**
 * Does this continuation carry on the same run of text, rather than starting a
 * new structural item? A newline in the separator, or a bullet marker at the
 * start of the next item, says the model's extra content is a SEPARATE entry —
 * a list, not the rest of a sentence.
 */
function continuesMidText(rest: string): boolean {
  const gap = /^\s*/.exec(rest)?.[0] ?? "";
  if (/[\n\r]/.test(gap)) return false;
  const next = rest.slice(gap.length);
  if (/^(?:[•·▪‣]|[-*–—]\s|\d+[.)]\s)/.test(next)) return false;
  return next.length > 0;
}

const EMPTY_BODY = {
  subjective: "",
  objective: "",
  assessment: "",
  plan: "",
  ros: "",
  pe: "",
  followUp: "",
} as const;

function toListedNote(
  note: ChartNoteRead,
  scraped: ElationNote[],
): ElationNote {
  const body = findScrapedBody(note, scraped);
  const m = note.body;

  // ── THE ORDERING RULE ────────────────────────────────────────────────────
  // THE MORE COMPLETE READ WINS, in every prose slot — see `preferComplete`,
  // which owns the rule and states it as an invariant. In practice that is the
  // scrape: it only loses when it is provably the same text, shortened.
  //
  // `body` is the feed scrape: Elation's OWN rendering of a signed note, paired
  // with this note by a unique date (or unique date + type) match — nothing
  // ambiguous ever matches, see `findScrapedBody`. `m` is the note's bullet-set
  // model, reconstructed category by category. When both have DIFFERENT answers
  // the scrape is the one the clinician actually charted and reads back in the
  // chart, so it wins. When the scrape's answer is a truncation of the model's,
  // the two agree about the content and the model simply has more of it.
  //
  // Ranking the SOURCES was the mistake, twice in opposite directions: the model
  // ahead of the scrape lost charted prose to a two-word structured list, and the
  // scrape unconditionally ahead of the model would trade a whole read for part
  // of one. Neither source is trustworthy by identity; completeness is checkable.
  //
  // WHY IT IS A RULE AND NOT A LIST OF EXCEPTIONS. This is the third fix for one
  // defect. Twice a change that made a model category readable where it had
  // always been empty — the multilist readers turning `problem`, and then its
  // sibling `assessplan`, from always-empty into usually-populated — silently
  // changed the meaning of an ordering decision written when those keys could
  // not have a value, and a two-word structured list replaced charted prose. The
  // first fix demoted `problem` BY NAME and left `assessplan` in front of the
  // scrape, so a signed `twoCol.complex2` note lost the clinician's entire
  // assessment ("HTN uncontrolled at 158/94 … start lisinopril 10mg daily")
  // and rendered "Hypertension\nType 2 diabetes" instead, with Plan blank.
  //
  // Fixing it key by key cannot hold: it has to be re-applied for every category
  // the next template, or the next accessor, makes readable, and nothing forces
  // that. So NO KEY NAME participates in the scrape-vs-model decision. The list
  // of keys below decides only WHICH SLOT a model category may fill; it can never
  // decide that a model category outranks the scrape, because the only thing that
  // can put a model value ahead of a scraped one is the scraped one being a
  // shortened form of it. Adding a key here, or a new accessor that starts
  // populating an existing key, cannot displace charted prose.
  //
  // A DRAFT is untouched by this: the feed renders signed notes only, so `body`
  // is null and the model is the only source there is — which is what the model
  // read was added for.
  const fromModel = (...keys: (keyof ChartNoteBody)[]): string => {
    for (const key of keys) {
      const value = m?.[key];
      if (typeof value === "string" && value) return value;
    }
    return "";
  };

  const subjective = preferComplete(
    body?.subjective,
    fromModel("hpi", "narrative", "problem"),
  );
  const objective = preferComplete(body?.objective, fromModel("objective"));
  // A COMBINED "Assessment & Plan" section (`assessplan` — the only A/P section
  // `twoCol.complex2` has) is rendered ONCE, under Assessment. It used to back
  // BOTH fields, so the identical paragraph appeared under two headings the
  // moment the multilist read made it non-empty. A dedicated `plan` category, or
  // a plan the feed scraped, still fills Plan on its own.
  const assessment = preferComplete(
    body?.assessment,
    fromModel("assessment", "assessplan"),
  );
  const plan = preferComplete(body?.plan, fromModel("plan"));
  const ros = preferComplete(body?.ros, fromModel("ros"));
  const pe = preferComplete(body?.pe, fromModel("pe"));
  const followUp = preferComplete(body?.followUp, fromModel("followup"));

  // `m.extra` holds text from categories we could not name, each labelled with
  // its own category name. It is a LAST RESORT for a note that would otherwise
  // render completely blank — on a `twoCol.complex2` draft the model's canonical
  // categories are empty and the feed has no row to match, and this is the only
  // thing standing between the clinician and nothing at all.
  //
  // Used only when EVERY other field came back empty. A two-column layout's
  // column-scoped category (`ElationNoteColOne`, read live) spans the S *and*
  // the A/P content of its column, so surfacing it under "Subjective" alongside
  // an Assessment that rendered correctly would both duplicate that text and
  // mis-attribute assessment prose to the subjective.
  const anyBody = !!(
    subjective ||
    objective ||
    assessment ||
    plan ||
    ros ||
    pe ||
    followUp
  );
  const lastResort = anyBody ? "" : m?.extra || "";

  return {
    // `status` keeps its existing three-value union so every downstream
    // consumer (including `@astrana/ui`'s note snapshot, which puts this value
    // on the wire) still type-checks. "on-hold" is an unsigned note, so it maps
    // to "draft" here and the finer state travels in `noteStatus`.
    status: note.signed ? "signed" : "draft",
    noteStatus: chartNoteStatus(note),
    noteId: note.id,
    templateName: note.templateName || undefined,
    visitDate: note.displayDate || note.documentDate,
    // The heading the panel shows. The template name is the honest answer to
    // "what kind of note is this". `description` FIRST because it is what the
    // chart itself shows ("Office Visit Note" / "Unsigned Office Visit Note",
    // live-verified) — the template name is now empty whenever Elation gave a
    // layout class, and the feed scrape's `visitType` exists only for signed
    // notes.
    visitType: note.templateName || note.description || body?.visitType || "",
    provider: note.physician || note.signUserName || body?.provider || "",
    // `description` FIRST because it is the chart's own LABEL for the note
    // ("Office Visit Note"), a title rather than a competing account of the
    // visit. Everything after it is content, and there the same completeness rule
    // as the prose slots applies: the scrape, then the model, and only then
    // `getHeaderSummary()` / `getNoteSummary()`.
    //
    // THOSE TWO ARE MODEL ACCESSORS. A previous round hoisted them ahead of the
    // scrape on a SOURCE-IDENTITY argument ("they come off the note object") —
    // precisely the reasoning that reversed this precedence three times — and a
    // one-word "Follow-up" summary then displaced the charted chest-pain prose.
    // Source identity decides nothing here; completeness does.
    examReason:
      note.description ||
      preferComplete(body?.examReason, fromModel("reason")) ||
      note.headerSummary ||
      note.noteSummary ||
      "",
    // Composed above, where the ordering and its rationale live. The model is
    // the only source that works for an unsigned draft, and it needs no date
    // match — the body comes off the note itself rather than being paired with a
    // scraped row.
    subjective: subjective || lastResort || EMPTY_BODY.subjective,
    objective: objective || EMPTY_BODY.objective,
    assessment: assessment || EMPTY_BODY.assessment,
    plan: plan || EMPTY_BODY.plan,
    ros: ros || EMPTY_BODY.ros,
    pe: pe || EMPTY_BODY.pe,
    followUp: followUp || EMPTY_BODY.followUp,
    vitals: body?.vitals ?? {},
    problemList: body?.problemList ?? [],
  };
}

/**
 * Build the list the Notes tab renders.
 *
 * Preference order, and the whole point of the change:
 *  - el8 model read AVAILABLE → the model is the source of truth (it has the
 *    unsigned drafts and is ready before the feed renders), with bodies merged
 *    in from the feed scrape where they can be matched.
 *  - read UNAVAILABLE → the DOM-scraped list, unchanged. Nothing regresses.
 *
 * `openNoteId` (the DOM-derived id of the note currently being edited) drops
 * that note from the list: the panel renders the open note separately, from the
 * live scrape, which has its actual in-progress body.
 */
export function buildChartNotes(
  read: ChartNotesReadResult | null | undefined,
  scraped: ElationNote[],
  openNoteId?: string,
): ElationNote[] {
  // The read POSITIVELY identified the page as another patient's chart. The
  // scraped feed came from that same page in the same tick, so its rows belong
  // to that other patient too — falling back to them is exactly the
  // cross-patient listing the guard refused. Fail closed: no list at all
  // (status `"unread"`, the panel says it is still reading) rather than one
  // attributed to the wrong chart. `"unreadable"` is different — identity is
  // UNKNOWN there, not wrong — and keeps the long-standing feed fallback.
  if (read?.failure === "patient-mismatch") return [];
  const feed = Array.isArray(scraped) ? scraped : [];
  if (!read?.available || !Array.isArray(read.notes)) return feed;

  const skipId = openNoteId || read.openNoteId || "";
  return read.notes
    .filter(shouldListChartNote)
    .filter((n) => !skipId || n.id !== skipId)
    .map((n) => toListedNote(n, feed))
    .map((note, index) => ({ note, index, time: parseDate(note.visitDate) }))
    // Most recent first; undated notes sink to the bottom keeping their
    // original relative order, so the list is stable across polls.
    .sort((a, b) => {
      if (a.time === null && b.time === null) return a.index - b.index;
      if (a.time === null) return 1;
      if (b.time === null) return -1;
      if (a.time !== b.time) return b.time - a.time;
      return a.index - b.index;
    })
    .map((e) => e.note);
}
